import type { Dispatch, ReactNode, SetStateAction } from "react"
import { createContext, useContext, useMemo } from "react"

import { DEFAULT_ENTRIES_LIMIT } from "../../../api/hooks/vote"
import { useVoting } from "./vote-context"

interface OffsetCtxProps {
	paginationOffset: Maybe<number>
	queryOffset: number | null
	setQueryOffset: Dispatch<SetStateAction<number | null>>
	totalPages: number
	currentPage: number
}

interface OffsetProviderProps {
	paginationOffset: Maybe<number>
	queryOffset: number | null
	setQueryOffset: Dispatch<SetStateAction<number | null>>
	children: ReactNode
}

const OffsetContext = createContext({} as OffsetCtxProps)

const OffsetContextProvider = ({
	queryOffset,
	paginationOffset,
	setQueryOffset,
	children,
}: OffsetProviderProps) => {
	const { totalEntries } = useVoting()

	const numericOffset = paginationOffset ?? 0

	const limit = numericOffset
		? Math.min(DEFAULT_ENTRIES_LIMIT, numericOffset)
		: DEFAULT_ENTRIES_LIMIT

	const totalPages = Math.ceil(totalEntries / limit)
	const currentPage = Math.ceil(numericOffset / DEFAULT_ENTRIES_LIMIT + 1)

	const contextData = useMemo(
		() => ({
			queryOffset,
			setQueryOffset,
			paginationOffset,
			totalPages,
			currentPage,
		}),
		[
			currentPage,
			paginationOffset,
			queryOffset,
			setQueryOffset,
			totalPages,
		],
	)

	return (
		<OffsetContext.Provider value={contextData}>
			{children}
		</OffsetContext.Provider>
	)
}

const useOffset = () => useContext(OffsetContext)

export { OffsetContextProvider, useOffset }
